/* eslint-disable @typescript-eslint/no-explicit-any */
import { all, call, put, select, takeEvery } from 'redux-saga/effects'
import { RequestStatus } from 'features/common'
import {
  planLimitRequest,
  accountLimitRequest,
  setPlanLimitStatus,
  setAccountLimitStatus,
  requestSubscription,
  setRequestSubscriptionStatus,
  cancelRequestSubscription,
  requestAccessRequest,
  setRequestAccessStatus,
  requestContactMeRequest,
  setRequestContactMeStatus
} from './hubspotSlice'
import { User } from 'features/users'
import { postHubspotLimitForm, postHubspotRequestSubscriptionForm } from '../api/hubspot'
import { QuotasRequestOptions, RequestSubscriptionHubspotFormProps, STORAGE_CONTACT_ME_REQUEST } from '../types'
import { PayloadAction } from '@reduxjs/toolkit'
import settings from 'settings'
import { trackRequestSubscriptionRequest } from 'features/data-observatory/api/dataObservatory'
import { DataExplorerTable, CatalogEntityTypes } from 'features/data-explorer/types'
import { ProviderType } from 'features/connections'
import { STORAGE_REQUEST_ACCESS, TableConnectionProp } from 'features/data-observatory/types'
import { getLocalStorageItem, setLocalStorageItem } from 'app/api/localStorage'
import { getProvider } from 'features/connections/providers'

function* sendCancelSubscriptionRequest(action: PayloadAction<RequestSubscriptionHubspotFormProps>) {
  try {
    yield put(setRequestSubscriptionStatus(RequestStatus.Pending))
    yield call(postHubspotRequestSubscriptionForm, yield getSubscriptionForm(action.payload, true))
    yield put(setRequestSubscriptionStatus(RequestStatus.Fulfilled))
    yield put(setRequestSubscriptionStatus(RequestStatus.Idle))
  } catch (e: any) {
    yield put(setRequestSubscriptionStatus(RequestStatus.Rejected))
  }
}

function* sendSubscriptionRequest(action: PayloadAction<RequestSubscriptionHubspotFormProps>) {
  try {
    const user: User = yield select((state) => state.users.me)

    yield put(setRequestSubscriptionStatus(RequestStatus.Pending))
    yield call(postHubspotRequestSubscriptionForm, yield getSubscriptionForm(action.payload))
    yield call(trackRequestSubscriptionRequest, { user, datasetId: action.payload.dataset.id })
    yield put(setRequestSubscriptionStatus(RequestStatus.Fulfilled))
    yield put(setRequestSubscriptionStatus(RequestStatus.Idle))
  } catch (e: any) {
    yield put(setRequestSubscriptionStatus(RequestStatus.Rejected))
  }
}

function* sendAccessRequest(action: PayloadAction<TableConnectionProp>) {
  const { table, connection } = action.payload
  try {
    yield put(setRequestAccessStatus(RequestStatus.Pending))
    yield call(postHubspotRequestSubscriptionForm, yield getAccessInForm(table, connection.provider_id))
    const requested = yield getLocalStorageItem(STORAGE_REQUEST_ACCESS) || {}
    requested[connection.id] = [...(requested[connection.id] || []), table.id]
    yield setLocalStorageItem(STORAGE_REQUEST_ACCESS, requested)
    yield put(setRequestAccessStatus(RequestStatus.Fulfilled))
  } catch (e) {
    yield put(setRequestAccessStatus(RequestStatus.Rejected))
  }
}

function* sendContactMeRequest(action: PayloadAction<{ feature: string }>) {
  const { feature } = action.payload
  try {
    yield put(setRequestContactMeStatus(RequestStatus.Pending))
    yield call(postHubspotLimitForm, yield getContactMeForm(feature))
    const contactMeRequest = yield getLocalStorageItem(STORAGE_CONTACT_ME_REQUEST) || {}
    yield setLocalStorageItem(STORAGE_CONTACT_ME_REQUEST, { ...contactMeRequest, [feature]: true })
    yield put(setRequestContactMeStatus(RequestStatus.Fulfilled))
    yield put(setRequestContactMeStatus(RequestStatus.Idle))
  } catch (e) {
    yield put(setRequestAccessStatus(RequestStatus.Rejected))
  }
}

function* sendPlanLimitForm() {
  try {
    yield put(setPlanLimitStatus(RequestStatus.Pending))
    yield call(postHubspotLimitForm, yield getLimitForm(QuotasRequestOptions.EndingTrial))
    yield put(setPlanLimitStatus(RequestStatus.Fulfilled))
  } catch (e: any) {
    yield put(setPlanLimitStatus(RequestStatus.Rejected))
  }
}

function* sendAccountLimitForm(action: PayloadAction<QuotasRequestOptions>) {
  try {
    const quotaRequest = action?.payload || QuotasRequestOptions.ReachingQuotas
    yield put(setAccountLimitStatus(RequestStatus.Pending))
    yield call(postHubspotLimitForm, yield getLimitForm(quotaRequest))
    yield put(setAccountLimitStatus(RequestStatus.Fulfilled))
  } catch (e: any) {
    yield put(setAccountLimitStatus(RequestStatus.Rejected))
  }
}

function* getSubscriptionForm({ dataset, datasetType }: RequestSubscriptionHubspotFormProps, cancel?: boolean) {
  const spatialDataCatalogPublicUrl = settings.publicCatalogUrl
  try {
    const { email }: User = yield select((state) => state.users.me)
    return {
      email,
      dataset_id: dataset.id,
      dataset_url: `${spatialDataCatalogPublicUrl}/${datasetType}/${dataset.slug}`,
      datastream_name: dataset.name,
      datastream_request_status: cancel ? 'cancel' : 'active',
      country_data: dataset.countryName,
      data_category: dataset.categoryName,
      provider: dataset.providerName
    }
  } catch (e: any) {
    throw new Error(e)
  }
}

function* getLimitForm(quotaOption: QuotasRequestOptions) {
  try {
    const { email }: User = yield select((state) => state.users.me)
    return {
      email,
      carto_3_quotas_request: quotaOption
    }
  } catch (e: any) {
    throw new Error(e)
  }
}

function* getContactMeForm(feature: string) {
  try {
    const { email }: User = yield select((state) => state.users.me)
    return {
      email,
      carto_3_quotas_request: feature
    }
  } catch (e: any) {
    throw new Error(e)
  }
}

function* getAccessInForm(table: DataExplorerTable, providerType: ProviderType) {
  const cloud = getProvider(providerType).label
  const spatialDataCatalogPublicUrl = settings.publicCatalogUrl
  const isGeography = !table.subscription?.associatedGeography.id
  const datasetType = isGeography ? CatalogEntityTypes.Geography : CatalogEntityTypes.Dataset

  try {
    const { email }: User = yield select((state) => state.users.me)
    return {
      email,
      dataset_id: table.id,
      dataset_url: `${spatialDataCatalogPublicUrl}/${datasetType}/${table.slug}`,
      datastream_name: table.name,
      data_request_type: 'Access',
      data_cloud_access: cloud,
      country_data: 'na',
      data_category: 'na',
      provider: 'na'
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    throw new Error(e)
  }
}

function* watchHubspot() {
  yield takeEvery(planLimitRequest.type, sendPlanLimitForm)
  yield takeEvery(accountLimitRequest.type, sendAccountLimitForm)
  yield takeEvery(requestSubscription.type, sendSubscriptionRequest)
  yield takeEvery(cancelRequestSubscription.type, sendCancelSubscriptionRequest)
  yield takeEvery(requestAccessRequest.type, sendAccessRequest)
  yield takeEvery(requestContactMeRequest.type, sendContactMeRequest)
}

export default function* hubspotSaga() {
  try {
    yield all([watchHubspot()])
  } catch (e: any) {
    //TODO
  }
}
